import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { NAV_ITEMS, LOGO_URL } from '../../../constants';
import { Page } from '../../types';
import { useAuth } from '@/contexts/AuthContext';

interface SidebarProps {
  currentPage: Page;
  onNavigate: (page: Page) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isCollapsed?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ currentPage, onNavigate, isOpen, setIsOpen, isCollapsed = false }) => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sidebarVariants = {
    mobileClosed: { x: '-100%' },
    mobileOpen: { x: 0 },
    desktop: { x: 0 }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && !isDesktop && (
        <div
          className="fixed inset-0 bg-black/20 z-[60] lg:hidden backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar Container */}
      <motion.aside
        initial={false}
        animate={isDesktop ? 'desktop' : isOpen ? 'mobileOpen' : 'mobileClosed'}
        variants={sidebarVariants}
        transition={{ type: "tween", ease: "linear", duration: 0.1 }}
        className={`fixed inset-y-0 left-0 bg-white border-r border-gray-100 shadow-xl lg:shadow-none z-[70] flex flex-col lg:static ${isDesktop ? 'lg:translate-x-0' : ''} transition-all ease-linear duration-200 ${(isCollapsed && isDesktop) ? 'w-20' : 'w-60 lg:w-48 xl:w-60'}`}
      >
        {/* Logo Section */}
        <div className="h-20 flex items-center justify-center border-b border-gray-50/50 transition-all duration-300">
          <img src={LOGO_URL} alt="Clip Logo" className={`${(isCollapsed && isDesktop) ? 'h-6' : 'h-9'} w-auto object-contain transition-all duration-300`} />
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 space-y-1 py-6 custom-scrollbar">
          {NAV_ITEMS.filter(item => {
            // Billing e Analytics sono riservati al titolare (owner = admin)
            if ((item.id as string) === 'BILLING' && !isAdmin) return false;
            if ((item.id as string) === 'ANALYTICS' && !isAdmin) return false;
            if ((item.id as string) === 'ASSISTANT' && !isAdmin) return false;
            return true;
          }).map((item) => {
            const isActive = currentPage === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  onNavigate(item.id);
                  if (!isDesktop) setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 group relative
                  ${isActive
                    ? 'text-clip-500 bg-clip-50'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                title={(isCollapsed && isDesktop) ? t(`sidebar.${item.id}`) : undefined}
              >
                <item.icon
                  size={20}
                  className={`transition-colors duration-200 flex-shrink-0 ${isActive ? 'text-clip-500' : 'text-gray-400 group-hover:text-gray-600'}`}
                />
                <AnimatePresence>
                  {!(isCollapsed && isDesktop) && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: "auto" }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.2 }}
                      className="truncate"
                    >
                      {t(`sidebar.${item.id}`)}
                    </motion.span>
                  )}
                </AnimatePresence>

                {/* Active Indicator */}
                {isActive && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute left-0 w-1 h-6 bg-clip-500 rounded-r-full"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </button>
            );
          })}
        </nav>

      </motion.aside>
    </>
  );
};

export default Sidebar;